import type {IGallerySantaProps, IPropsInjectedByViewerScript} from '../types/galleryTypes';
import {IHostProps} from '@wix/native-components-infra/dist/es/src/types/types';
import {IStylesContextValue} from '@wix/tpa-settings';
import {IStylesParams} from '../styleParams/types';
import {ReadOnlyExperiments} from '@wix/wix-experiments/dist/src/ReadOnlyExperiments';

type ProvidedGlobalPropNames =
  | 'allowFreeProducts'
  | 'addedToCartStatus'
  | 'clearFilters'
  | 'currentPage'
  | 'useCategories'
  | 'scrollToProduct'
  | 'totalPages'
  | 'maxProductsPerPage'
  | 'linkForAllPages'
  | 'nextPrevLinks'
  | 'experiments'
  | 'filterModels'
  | 'filterProducts'
  | 'applyFilteredProductsOnMobile'
  | 'focusedProductIndex'
  | 'getCategoryProducts'
  | 'handleAddToCart'
  | 'sendSortClickBiEvent'
  | 'sendClickShippingInfoLinkSf'
  | 'handleCategoryClick'
  | 'handleCategoryBreadcrumbsClick'
  | 'handleCategoryClampClick'
  | 'handlePagination'
  | 'reportProductItemClick'
  | 'handleProductItemClick'
  | 'handleProductsOptionsChange'
  | 'hasMoreProductsToLoad'
  | 'hasSelectedFilters'
  | 'htmlTags'
  | 'host'
  | 'imagePosition'
  | 'imageMode'
  | 'imageRatio'
  | 'isAutoGrid'
  | 'isCategoryPage'
  | 'isFirstPage'
  | 'isHorizontalLayout'
  | 'isInteractive'
  | 'isLiveSiteMode'
  | 'isPreviewMode'
  | 'isGalleryRowsAndColsWereModified'
  | 'shouldShowMobile'
  | 'isCategoryVisible'
  | 'isEditorMode'
  | 'isRTL'
  | 'isSEO'
  | 'isSSR'
  | 'loadMoreProducts'
  | 'loadMoreType'
  | 'mainCollectionId'
  | 'numberOfSelectedFilterTypes'
  | 'openQuickView'
  | 'paginationMode'
  | 'priceBreakdown'
  | 'productsManifest'
  | 'products'
  | 'productsVariantInfoMap'
  | 'productsPriceRangeMap'
  | 'selectedSort'
  | 'shouldAlternateImagePosition'
  | 'shouldShowAddToCartSuccessAnimation'
  | 'shouldShowClearFilters'
  | 'shouldShowMobileFiltersModal'
  | 'shouldShowSort'
  | 'shouldShowImageCarousel'
  | 'shouldShowProductOptions'
  | 'shouldUseAutoGridProductsCount'
  | 'showShowLightEmptyState'
  | 'setSelectedSort'
  | 'sortProducts'
  | 'sortingOptions'
  | 'textsMap'
  | 'toggleFiltersModalVisibility'
  | 'totalProducts'
  | 'updateAddToCartStatus'
  | 'usingNewGalleryStyles'
  | 'productsRequestInProgress'
  | 'currentCategory'
  | 'categories'
  | 'slots'
  | 'breadcrumbsHistory'
  | 'taxonomicalBreadcrumbs'
  | 'isOptionsRevealEnabled'
  | 'mobileFiltersPanelState'
  | 'allProductsCategoryId';

export type IGlobals = Pick<IPropsInjectedByViewerScript & IGallerySantaProps, ProvidedGlobalPropNames> & {
  styles: IStylesContextValue;
  stylesParams: IStylesParams;
  useExperiments: ReadOnlyExperiments;
} & Pick<IHostProps, 'updateLayout' | 'dimensions' | 'appLoadBI' | 'accessibilityEnabled'> & {compId: string};

export interface IGalleryGlobalProps {
  globals?: IGlobals;
}

export const galleryGlobalsStrategy = (props: IPropsInjectedByViewerScript & IGallerySantaProps) => {
  const {
    allowFreeProducts,
    addedToCartStatus,
    clearFilters,
    currentPage,
    useCategories,
    scrollToProduct,
    totalPages,
    maxProductsPerPage,
    linkForAllPages,
    nextPrevLinks,
    experiments,
    filterModels,
    filterProducts,
    applyFilteredProductsOnMobile,
    focusedProductIndex,
    getCategoryProducts,
    handleAddToCart,
    sendClickShippingInfoLinkSf,
    sendSortClickBiEvent,
    handleCategoryClick,
    handleCategoryBreadcrumbsClick,
    handleCategoryClampClick,
    handlePagination,
    reportProductItemClick,
    handleProductItemClick,
    handleProductsOptionsChange,
    hasMoreProductsToLoad,
    hasSelectedFilters,
    htmlTags,
    imagePosition,
    imageMode,
    imageRatio,
    isAutoGrid,
    isCategoryPage,
    isFirstPage,
    isHorizontalLayout,
    isInteractive,
    isLiveSiteMode,
    isPreviewMode,
    shouldShowMobile,
    isCategoryVisible,
    isEditorMode,
    isRTL,
    isSEO,
    isSSR,
    isGalleryRowsAndColsWereModified,
    loadMoreProducts,
    loadMoreType,
    mainCollectionId,
    numberOfSelectedFilterTypes,
    openQuickView,
    paginationMode,
    priceBreakdown,
    productsManifest,
    products,
    productsVariantInfoMap,
    productsPriceRangeMap,
    selectedSort,
    shouldAlternateImagePosition,
    shouldShowAddToCartSuccessAnimation,
    shouldShowClearFilters,
    shouldShowMobileFiltersModal,
    shouldUseAutoGridProductsCount,
    shouldShowSort,
    showShowLightEmptyState,
    shouldShowImageCarousel,
    shouldShowProductOptions,
    setSelectedSort,
    sortProducts,
    sortingOptions,
    textsMap,
    toggleFiltersModalVisibility,
    totalProducts,
    updateAddToCartStatus,
    usingNewGalleryStyles,
    productsRequestInProgress,
    isOptionsRevealEnabled,
    currentCategory,
    breadcrumbsHistory,
    taxonomicalBreadcrumbs,
    categories,
    mobileFiltersPanelState,
    slots,
    ...locals
  } = props;

  const {appLoadBI, dimensions, updateLayout, id: compId, accessibilityEnabled} = props.host;
  return {
    globals: {
      accessibilityEnabled,
      allowFreeProducts,
      addedToCartStatus,
      appLoadBI,
      clearFilters,
      currentPage,
      useCategories,
      scrollToProduct,
      totalPages,
      maxProductsPerPage,
      linkForAllPages,
      nextPrevLinks,
      compId,
      dimensions,
      experiments,
      filterModels,
      filterProducts,
      applyFilteredProductsOnMobile,
      focusedProductIndex,
      getCategoryProducts,
      handleAddToCart,
      handleCategoryClick,
      handleCategoryBreadcrumbsClick,
      handleCategoryClampClick,
      handlePagination,
      reportProductItemClick,
      handleProductItemClick,
      handleProductsOptionsChange,
      hasMoreProductsToLoad,
      hasSelectedFilters,
      host: props.host,
      htmlTags,
      imagePosition,
      imageMode,
      imageRatio,
      isAutoGrid,
      isCategoryPage,
      isFirstPage,
      isHorizontalLayout,
      isInteractive,
      isLiveSiteMode,
      isPreviewMode,
      shouldShowMobile,
      isCategoryVisible,
      isEditorMode,
      isRTL,
      isSEO,
      isSSR,
      isGalleryRowsAndColsWereModified,
      loadMoreProducts,
      loadMoreType,
      mainCollectionId,
      numberOfSelectedFilterTypes,
      openQuickView,
      paginationMode,
      priceBreakdown,
      productsManifest,
      products,
      productsVariantInfoMap,
      productsPriceRangeMap,
      selectedSort,
      sendSortClickBiEvent,
      sendClickShippingInfoLinkSf,
      shouldAlternateImagePosition,
      shouldShowAddToCartSuccessAnimation,
      shouldShowClearFilters,
      shouldShowMobileFiltersModal,
      shouldShowSort,
      shouldUseAutoGridProductsCount,
      showShowLightEmptyState,
      shouldShowImageCarousel,
      shouldShowProductOptions,
      setSelectedSort,
      sortProducts,
      sortingOptions,
      slots,
      textsMap,
      toggleFiltersModalVisibility,
      totalProducts,
      updateLayout,
      updateAddToCartStatus,
      usingNewGalleryStyles,
      productsRequestInProgress,
      isOptionsRevealEnabled,
      currentCategory,
      breadcrumbsHistory,
      taxonomicalBreadcrumbs,
      categories,
      mobileFiltersPanelState,
    },
    locals,
  };
};
